import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import safeLogger from "src/services/safeLogger";
import { findParedFile, getFileCellValues } from "./helpers";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import clsx from "clsx";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import { Cancel, Save as SaveIcon } from "@material-ui/icons";
import { HPOTerms, PrimaryHistology, SingleGene } from ".";
import { CustomDropdown, CustomTextField } from "./sampleFilesTable";

const useStyles = makeStyles((theme) => ({
  hidden: {
    display: "none",
  },
  metaSummary: {
    marginBottom: "20px",
    display: "flex",
    "& p": {
      display: "inline-flex",
    },
  },
  alert: {},
  infoContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1px",
    "& span:first-child": {
      fontSize: "0.820rem",
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    "& .MuiChip-root": {
      height: "28px",
      fontSize: "0.8125rem",
    },
  },
}));

export const FusionSampleFileTable = (props) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [formError, setFormError] = useState([]);
  const {
    appConf,
    formData,
    files,
    fileMeta,
    uploadTabValue,
    setFileMeta,
    phase,
    setPhase,
  } = props;

  const [headers, setHeaders] = useState(null);
  const [fileNames, setFileNames] = useState(null);
  const [changed, setChanged] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const mgiTableHeaders = {
    flowcell: "Flowcell",
    lane: "Lane",
    sample_name: t("Sample"),
  };

  const illuminaTableHeaders = {
    sample_name: t("Sample Name"),
    sample_number: t("Sample Number"),
    lane: "Lane",
  };

  useEffect(() => {
    if (fileMeta.length && formError.length !== fileMeta.length) {
      safeLogger(fileMeta);
      for (let i = 0; i < fileMeta.length; i++) {
        formError.push({});
      }
      setFormError(formError.slice(0));
    }
  }, [fileMeta]);

  useEffect(() => {
    if (headers) {
      let tmpFileObjects = [];

      if (formData.sample_kind === "paired_end") {
        let pairedFiles = [];

        for (let i = 0; i < files.length; i++) {
          if (!pairedFiles.includes(files[i].name)) {
            let fileObj = {};
            const cellValues = getFileCellValues(files[i].name);
            if (cellValues) {
              Object.keys(headers).map(
                (headItem, index) => (fileObj[headItem] = cellValues[index]),
              );
            }
            const pairFileName = findParedFile(files[i].name, files);
            if (pairFileName) {
              fileObj["pair_1"] =
                uploadTabValue === "1"
                  ? {
                      name: files[i].name,
                      flowIdentifier: files[i]["flowIdentifier"],
                      byte_size: files[i]["size"],
                      annexes: [],
                    }
                  : {
                      name: files[i].name,
                      flowIdentifier: files[i]["flowIdentifier"],
                      byte_size: files[i]["size"],
                      annexes: [],
                    };
              fileObj["pair_2"] =
                uploadTabValue === "1"
                  ? {
                      name: pairFileName.name,
                      flowIdentifier: pairFileName.flowIdentifier,
                      byte_size: pairFileName["size"],
                      annexes: [],
                    }
                  : {
                      name: pairFileName.name,
                      flowIdentifier: pairFileName.flowIdentifier,
                      byte_size: pairFileName["size"],
                      annexes: [],
                    };
              fileObj["flowIdentifier"] = files[i]["flowIdentifier"];
              fileObj["calculate_frequency"] = true;
              fileObj["merged"] = [];
              tmpFileObjects.push(fileObj);
              pairedFiles.push(files[i].name);
              pairedFiles.push(pairFileName.name);
              fileNames[files[i].name] = true;
              fileNames[pairFileName.name] = true;
            }
          }
        }

        if (pairedFiles.length !== files.length) {
          for (let i = 0; i < (files.length - pairedFiles.length) / 2; i++) {
            tmpFileObjects.push({});
          }
        }

        setFileMeta([...tmpFileObjects]);
      } else {
        for (let i = 0; i < files.length; i++) {
          let fileObj = {};
          const cellValues = getFileCellValues(files[i].name);
          if (cellValues) {
            Object.keys(headers).map(
              (headItem, index) => (fileObj[headItem] = cellValues[index]),
            );
          }
          fileObj["byte_size"] = files[i].size;
          fileObj["filename"] = files[i].name;
          tmpFileObjects.push(fileObj);
        }
        setFileMeta(tmpFileObjects);
      }
    }
  }, [headers]);

  useEffect(() => {
    if (phase === "sampleMeta") {
      if (!headers) {
        setHeaders(
          formData.sequencer === "mgi" ? mgiTableHeaders : illuminaTableHeaders,
        );
      }

      let tmpFileNames = {};
      for (let i = 0; i < files.length; i++) {
        tmpFileNames[files[i].name] = false;
      }
      setFileNames(tmpFileNames);
    }
  }, [phase]);

  const isInputFormatValid = () => {
    for (let i = 0; i < formError.length; i++) {
      const formErrorKeys = Object.keys(formError[i]);
      for (let j = 0; j < formErrorKeys.length; j++) {
        if (formError[i][formErrorKeys[j]]) return false;
      }
    }

    return true;
  };

  const isFileConfigUnique = () => {
    const configValues = [];
    for (let i = 0; i < fileMeta.length; i++) {
      let fileConfig = null;
      if (formData.sequencer === "mgi") {
        fileConfig =
          fileMeta[i].flowcell + fileMeta[i].lane + fileMeta[i].sample_name;
      } else {
        fileConfig =
          fileMeta[i].sample_name +
          fileMeta[i].sample_number +
          fileMeta[i].lane;
      }
      if (!configValues.includes(fileConfig)) {
        configValues.push(fileConfig);
      } else {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isInputFormatValid()) {
      if (isFileConfigUnique()) {
        setPhase("credit_calculate");
      } else {
        enqueueSnackbar(
          t(
            "form_conf_not_unique_message",
            "Form configuration is not unique. Please check and try again.",
          ),
          {
            variant: "error",
          },
        );
      }
    } else {
      enqueueSnackbar(
        t(
          "form_input_not_valid_message",
          "Form input are not valid. Please check and try again.",
        ),
        {
          variant: "error",
        },
      );
    }
  };

  const handleMerge = async () => {
    setLoading(false);
    let firstID = undefined;
    let firstSample = undefined;
    const mergeSamples = [];
    let newFileMeta = fileMeta;
    for (let i = 0; i < newFileMeta.length; i++) {
      if (newFileMeta[i].seperate) {
        newFileMeta[i]["seperate"] = false;
        if (!firstSample && !firstID) {
          firstSample = newFileMeta[i];
          firstID = i;
        } else {
          mergeSamples.push(newFileMeta[i]);
          delete newFileMeta[i];
        }
      } else {
        newFileMeta[i]["seperate"] = false;
      }
    }
    newFileMeta = await newFileMeta.map((val) => {
      val.seperate = false;
      return val;
    });
    if (firstSample) {
      newFileMeta[firstID]["merged"] = mergeSamples;
      setFormError([
        ...newFileMeta.filter((value) => Boolean(value)).map((val) => ({})),
      ]);
      setChanged((prev) => !prev);
      setFileMeta([
        ...newFileMeta
          .filter((value) => Boolean(value))
          .map((val) => {
            val.seperate = false;
            return val;
          }),
      ]);
      setOpen(false);
    }
    setLoading(true);
  };

  return (
    <Grid item xs={12} className={clsx({ [classes.hidden]: phase === "init" })}>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        maxWidth="xl"
      >
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                {headers &&
                  Object.values(headers).map((item, index) => (
                    <TableCell key={index}>{t(item)}</TableCell>
                  ))}
                {formData.sample_kind === "paired_end" ? (
                  <>
                    <TableCell>{t("Pair 1")}</TableCell>
                    <TableCell>{t("Pair 2")}</TableCell>
                  </>
                ) : (
                  <TableCell>{t("Sample Name")}</TableCell>
                )}
                {formData.pipeline_class &&
                  (formData.pipeline_class === "germline" ||
                    formData.pipeline_class.includes("germline")) &&
                  formData.single_gene &&
                  (formData.single_gene === "pcr" ||
                    formData.single_gene.includes("pcr")) && (
                    <TableCell>{t("Genes")}</TableCell>
                  )}
                {formData.pipeline_class &&
                  (formData.pipeline_class === "germline" ||
                    formData.pipeline_class.includes("germline")) && (
                    <TableCell>{t("HPO Terms")}</TableCell>
                  )}
                {formData.pipeline_class &&
                  (formData.pipeline_class === "somatic" ||
                    formData.pipeline_class.includes("somatic")) && (
                    <>
                      <TableCell>{t("Tissues")}</TableCell>
                      <TableCell>{t("Histology")}</TableCell>
                    </>
                  )}
                <TableCell width="10px">
                  {t("Variant Frequency Calculation")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fileMeta.map((file, index) => {
                return (
                  file?.seperate && (
                    <FusionSampleFileTableRow
                      key={index}
                      isMerged={false}
                      uploadTabValue={uploadTabValue}
                      files={files}
                      index={index}
                      headers={headers}
                      file={file}
                      sequencerType={formData.sequencer}
                      sampleKind={formData.sample_kind}
                      fileMeta={fileMeta}
                      setFileMeta={setFileMeta}
                      formData={formData}
                      fileNames={fileNames}
                      setFileNames={setFileNames}
                      formError={formError}
                      setFormError={setFormError}
                    />
                  )
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMerge} color="primary" variant="contained">
            {t("Merge")}
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              setOpen(false);
            }}
            variant="contained"
          >
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
      <Box className={classes.metaSummary}>
        <div className={classes.infoContainer}>
          <span style={{ color: "#5e5e5e", marginLeft: "10px" }}>
            {t("Title")}:
          </span>
          <Chip
            style={{ marginLeft: "4px", marginRight: "20px" }}
            label={formData.name}
          />
        </div>
        <div className={classes.infoContainer}>
          <span style={{ color: "#5e5e5e" }}>{t("Type")}:</span>
          <Chip
            style={{ marginLeft: "4px", marginRight: "20px" }}
            label={t(formData.sample_kind.replace("_", "-"))}
          />
        </div>
        <div className={classes.infoContainer}>
          <span style={{ color: "#5e5e5e" }}>{t("Kit")}:</span>
          <Chip
            style={{ marginLeft: "4px", marginRight: "20px" }}
            label={appConf.entities.kits[formData.kit]?.label}
          />
        </div>
        <div className={classes.infoContainer}>
          <span style={{ color: "#5e5e5e" }}>{t("Sequencer")}:</span>
          <Chip
            style={{ marginLeft: "4px", marginRight: "20px" }}
            label={appConf.entities.sequencers[formData.sequencer]}
          />
        </div>
        <div className={classes.infoContainer}>
          <span style={{ color: "#5e5e5e" }}>{t("Pipeline")}:</span>
          <Chip
            style={{ marginLeft: "4px", marginRight: "20px" }}
            label={
              appConf.entities.pipeline_classes["exome_fastq"][
                formData.pipeline_class
              ] || formData.pipeline_class
            }
          />
        </div>
      </Box>
      {fileMeta.length ? (
        <form onSubmit={handleSubmit}>
          <Table>
            <TableHead>
              <TableRow>
                {headers &&
                  Object.values(headers).map((item, index) => (
                    <TableCell width="100px" key={index}>
                      {t(item)}
                    </TableCell>
                  ))}
                {formData.sample_kind === "paired_end" ? (
                  <>
                    <TableCell width="100px">{t("Pair 1")}</TableCell>
                    <TableCell width="100px">{t("Pair 2")}</TableCell>
                  </>
                ) : (
                  <TableCell width="100px">{t("File Name")}</TableCell>
                )}
                {formData.pipeline_class &&
                  (formData.pipeline_class === "germline" ||
                    formData.pipeline_class.includes("germline")) &&
                  formData.single_gene &&
                  (formData.single_gene === "pcr" ||
                    formData.single_gene.includes("pcr")) && (
                    <TableCell width="400px">{t("Genes")}</TableCell>
                  )}
                {formData.pipeline_class &&
                  (formData.pipeline_class === "germline" ||
                    formData.pipeline_class.includes("germline")) && (
                    <TableCell width="300px">{t("HPO Terms")}</TableCell>
                  )}
                {formData.pipeline_class &&
                  (formData.pipeline_class === "somatic" ||
                    formData.pipeline_class.includes("somatic")) && (
                    <>
                      <TableCell width="100px">{t("Tissues")}</TableCell>
                      <TableCell width="100px">{t("Histology")}</TableCell>
                    </>
                  )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                fileMeta.map((file, index) => {
                  return (
                    <FusionSampleFileTableRow
                      key={index}
                      index={index}
                      changed={changed}
                      files={files}
                      isMerged={true}
                      headers={headers}
                      sequencerType={formData.sequencer}
                      sampleKind={formData.sample_kind}
                      uploadTabValue={uploadTabValue}
                      file={file}
                      formData={formData}
                      fileMeta={fileMeta}
                      setFileMeta={setFileMeta}
                      fileNames={fileNames}
                      setFileNames={setFileNames}
                      formError={formError}
                      setFormError={setFormError}
                    />
                  );
                })}
            </TableBody>
          </Table>
          <Box display="flex" mt={5}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<NavigateBeforeIcon />}
              onClick={() => setPhase("init")}
            >
              {t("Back")}
            </Button>
            <Button
              type="submit"
              style={{ marginLeft: "auto" }}
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
            >
              {t("Submit")}
            </Button>
          </Box>
        </form>
      ) : (
        ""
      )}
    </Grid>
  );
};

const FusionSampleFileTableRow = (props) => {
  const {
    headers,
    sequencerType,
    sampleKind,
    index,
    fileMeta,
    setFileMeta,
    fileNames,
    files,
    file,
    changed,
    uploadTabValue,
    formData,
    isMerged,
    setFileNames,
    formError,
    setFormError,
  } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState();

  useEffect(() => {
    if (isMerged) {
      let newFileMeta = fileMeta.slice(0);
      newFileMeta[index].calculate_frequency = true;
      newFileMeta[index].seperate = false;
      file["seperate"] = false;
      setFileMeta([...newFileMeta]);
    }
  }, [changed]);

  const updateFileMeta = (name, value) => {
    if (
      ["pair_1", "pair_2"].includes(name) &&
      ["1", "2", "3", "4"].includes(uploadTabValue)
    ) {
      let tmpFileSize;
      for (const file of files) {
        if (file.name === value || file.name === value.name) {
          if (file.byte_size) tmpFileSize = file.byte_size;
          if (file.size) tmpFileSize = file.size;
        }
      }
      file[name] = { name: value.name, annexes: [], byte_size: tmpFileSize };
    } else {
      file[name] = value;
    }
    setFileMeta((prev) => [...prev]);
  };

  const handleDelete = (newFile) => {
    setFormError([...formError, {}]);
    file["merged"] = file["merged"].filter(
      (val) => newFile.flowcell !== val.flowcell,
    );
    setFileMeta((prev) => [...prev, newFile]);
  };

  return (
    <>
      <TableRow>
        {Object.keys(headers).map((header, i) => (
          <TableCell key={i}>
            <CustomTextField
              rowIndex={index}
              inputIndex={i}
              sequencerType={sequencerType}
              label={headers[header]}
              name={header}
              value={file[header] ? file[header] : ""}
              updateFileMeta={updateFileMeta}
              formError={formError}
              setFormError={setFormError}
            />
          </TableCell>
        ))}
        {sampleKind === "paired_end" ? (
          <>
            <TableCell>
              <CustomDropdown
                defaultValue={file["pair_1"] ? file["pair_1"] : ""}
                options={fileNames}
                setOptions={setFileNames}
                name={"pair_1"}
                labelTitle={t("Pair 1")}
                updateFileMeta={updateFileMeta}
                isMutuallyExclusive={true}
                isRequired={true}
                disabled={false}
              />
            </TableCell>
            <TableCell>
              <CustomDropdown
                defaultValue={file["pair_2"] ? file["pair_2"] : ""}
                options={fileNames}
                setOptions={setFileNames}
                name={"pair_2"}
                labelTitle={t("Pair 2")}
                updateFileMeta={updateFileMeta}
                isMutuallyExclusive={true}
                isRequired={true}
                disabled={false}
              />
            </TableCell>
          </>
        ) : (
          <TableCell>{file["filename"] ? file["filename"] : ""}</TableCell>
        )}
        <SingleGene
          data={fileMeta[index]}
          single_gene={formData.single_gene}
          pipeline_class={formData.pipeline_class}
          handleChange={updateFileMeta}
        />
        {formData.pipeline_class &&
          (formData.pipeline_class === "germline" ||
            formData.pipeline_class.includes("germline")) && (
            <HPOTerms
              data={fileMeta[index]}
              pipeline_class={formData.pipeline_class}
              handleChange={updateFileMeta}
            />
          )}
        <PrimaryHistology
          data={fileMeta[index]}
          pipeline_class={formData.pipeline_class}
          handleChange={updateFileMeta}
        />
      </TableRow>
      {file["merged"] && file["merged"].length > 0 && (
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={
              Object.keys(headers).length +
              (sampleKind === "paired_end" ? 4 : 3)
            }
          >
            <Collapse in={open} timeout={1} unmountOnExit>
              <Table>
                <TableHead style={{ height: 0 }}>
                  <TableRow>
                    <TableCell>"Flowcell"</TableCell>
                    <TableCell>"Flowcell"</TableCell>
                    <TableCell>{t("Sample Name")}</TableCell>
                    <TableCell>{t("Pair 1")}</TableCell>
                    <TableCell>{t("Pair 2")}</TableCell>
                    <TableCell>{t("Cancel")}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {file["merged"].map((file, index) => {
                    return (
                      <TableRow>
                        <TableCell>{file.flowcell}</TableCell>
                        <TableCell>{file.lane}</TableCell>
                        <TableCell>{file.sample_name}</TableCell>
                        <TableCell>{file.pair_1.name}</TableCell>
                        <TableCell>{file.pair_2.name}</TableCell>
                        <TableCell>
                          <IconButton
                            color="secondary"
                            onClick={(e) => handleDelete(file)}
                          >
                            <Cancel />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
